import { useCallback, useEffect } from 'react';
import Page from '../../../components/page/Page';
import Header from '../../../components/users/Header';
import { useUserCore } from '../../../../core/modules/user';
import { Avatar, Paper, Typography } from '@mui/material';
import useThemeMediaQuery from '../../../../core/hooks/useThemeMediaQuery';
import { useNavigationCore } from '../../../../core/router/navigation';
import clsx from 'clsx';
import { useMeCore } from '../../../../core/modules/me';
import { useTranslation } from 'react-i18next';
import UserDialog from '../../../components/users/UserDialog';
import RoleChip from '../../../components/RoleChip';
import { User } from '../../../../core/interfaces/user.interface';

const Users = () => {
  const { t } = useTranslation();
  const { user } = useMeCore();
  const { users, options, getUsers, clearUser } = useUserCore();
  const { goToUser, goToWelcome } = useNavigationCore();

  const isMobile = useThemeMediaQuery((theme) => theme.breakpoints.down('lg'));

  useEffect(() => {
    clearUser();
  }, [clearUser]);

  useEffect(() => {
    if (!user?.is_superuser) {
      goToWelcome();
    }
  }, [user, goToWelcome, clearUser]);

  useEffect(() => {
    getUsers(options);
  }, [getUsers, options]);

  const onSelecting = useCallback((id: string) => {
    goToUser(id);
  }, [goToUser]);

  return (
    <>
      <Page
        header={<Header />}
        content={
          !users.length ? (
            <div className="flex flex-1 items-center justify-center h-full">
              <Typography color="text.secondary" variant="h5">
                {t('users.label.no.users')}
              </Typography>
            </div>
          ) : (
            <div className="w-full my-16 sm:col-span-3 px-24 md:px-32">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-24 w-full min-w-0">
                {users.map((user : User) => (
                  <Paper
                    className="flex flex-col flex-auto items-center shadow rounded-2xl overflow-hidden cursor-pointer"
                    key={user.id}
                    onClick={() => onSelecting(user.id)}
                  >
                    <div className="flex flex-col flex-auto w-full p-32 items-center text-center">
                      {user.avatar ? (
                        <Avatar
                          alt="user photo"
                          className="w-128 h-128"
                          src={user.avatar}
                        />
                      ) : (
                        <Avatar className="w-128 h-128">
                          {user.full_name}
                        </Avatar>
                      )}
                      <div className="mt-24">
                        <RoleChip isSuperuser={user.is_superuser} role={user.role} />
                      </div>
                      <Typography className={clsx(
                        'font-medium',
                        user.active ? 'text-slate-950' : 'text-red-700'
                      )}>
                        {user.full_name}
                      </Typography>
                      <Typography>{user.email}</Typography>
                    </div>
                  </Paper>
                ))}
              </div>
            </div>
          )
        }
        scroll={isMobile ? 'normal' : 'content'}
      />
      <UserDialog />
    </>
  );
};

export default Users;